import type { ApolloClient } from '@apollo/client';
import { from, map, of } from 'rxjs';
import {
    ReportBuilderProfileLogoRuntimeDataDocument,
    type ReportBuilderProfileLogoRuntimeDataQuery,
    type ReportBuilderProfileLogoRuntimeDataQueryVariables,
    ReportTemplateEntityType,
} from '@/graphql/types';
import type { RenderContext } from '../../../elements/types';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { RuntimeDataService } from '../types';
import type { RuntimeDataServiceRequest, RuntimeResultLinkDescriptor } from '../types';
import {
    isRecord,
    normalizeNonEmptyString,
    normalizeRequiredId,
    normalizeString,
} from '../validationPrimitives';
import {
    createSystemImageAssetArgOptions,
    getOmnidotsLogoAsset,
    normalizeSystemImageAssetKey,
} from './assets';

type SystemAssetsRuntimeInput = RuntimeDataServiceInput<'system_assets'>;
type SystemAssetsRuntimeOutput = RuntimeDataServiceOutput<'system_assets'>;

function normalizeEntityType(value: unknown): ReportTemplateEntityType {
    if (
        value === ReportTemplateEntityType.Project ||
        value === ReportTemplateEntityType.MeasuringPoint
    ) {
        return value;
    }

    throw new Error('Expected "system_assets.entityType" to be a valid ReportTemplateEntityType.');
}

function validateSystemAssetsRuntimeInput(input: unknown): SystemAssetsRuntimeInput {
    if (!isRecord(input)) {
        throw new Error('Expected "system_assets" input to be an object.');
    }

    const assetKey = normalizeSystemImageAssetKey(input.assetKey, 'system_assets.assetKey');
    if (assetKey === 'omnidots_logo') {
        if (Object.keys(input).some((key) => key !== 'assetKey')) {
            throw new Error('Expected bundled "system_assets" input to only define "assetKey".');
        }
        return { assetKey };
    }
    if (Object.keys(input).some((key) => !['assetKey', 'entityType', 'entityId'].includes(key))) {
        throw new Error(
            'Expected profile-logo "system_assets" input to only define "assetKey", "entityType", and "entityId".'
        );
    }

    return {
        assetKey,
        entityType: normalizeEntityType(input.entityType),
        entityId: normalizeRequiredId(input.entityId, 'system_assets.entityId'),
    };
}

function validateSystemAssetsRuntimeResult(result: unknown): SystemAssetsRuntimeOutput {
    if (result === null) {
        return null;
    }
    if (!isRecord(result)) {
        throw new Error('Expected "system_assets" result to be an object or null.');
    }

    return {
        url: normalizeNonEmptyString(result.url, 'system_assets.url'),
        caption: normalizeString(result.caption, 'system_assets.caption'),
    };
}

function resolveProfileLogoInput(context?: RenderContext): SystemAssetsRuntimeInput {
    if (!context) {
        throw new Error('Profile-logo system asset requires render context.');
    }
    if (context.kind === 'project') {
        return {
            assetKey: 'profile_logo',
            entityType: ReportTemplateEntityType.Project,
            entityId: context.project.id,
        };
    }
    if (context.kind === 'measuring-point') {
        return {
            assetKey: 'profile_logo',
            entityType: ReportTemplateEntityType.MeasuringPoint,
            entityId: context.measuringPoint.id,
        };
    }

    throw new Error('Profile-logo system asset received unsupported render context.');
}

export class SystemAssetsRuntimeDataService extends RuntimeDataService<'system_assets'> {
    static readonly serviceId = 'system_assets' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_SYSTEM_ASSETS_LABEL'),
            description: gettext('DATA_SERVICE_SYSTEM_ASSETS_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'image',
                label: gettext('DATA_SERVICE_SYSTEM_ASSETS_IMAGE_LABEL'),
                description: gettext('DATA_SERVICE_SYSTEM_ASSETS_IMAGE_DESCRIPTION'),
                supportedContextKinds: ['project', 'measuring-point'],
                supportedSwarmTypes: null,
                path: '',
                valueKind: 'image',
                argPolicy: 'required',
                argOptions: createSystemImageAssetArgOptions(),
                exposeInInsertionPresets: true,
                buildInput: ({ arg, context }) => {
                    const assetKey = normalizeSystemImageAssetKey(arg, 'system_assets.image.arg');
                    return assetKey === 'profile_logo'
                        ? resolveProfileLogoInput(context)
                        : { assetKey };
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptor<'system_assets'>[];
    }

    validateArgs = validateSystemAssetsRuntimeInput;

    validateResult = validateSystemAssetsRuntimeResult;

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    createResult$ = ({ input }: RuntimeDataServiceRequest<'system_assets'>) => {
        if (input.assetKey === 'omnidots_logo') {
            return of(getOmnidotsLogoAsset());
        }

        const variables: ReportBuilderProfileLogoRuntimeDataQueryVariables = {
            entityType: input.entityType,
            entityId: input.entityId,
        };
        return from(
            this.client.query<
                ReportBuilderProfileLogoRuntimeDataQuery,
                ReportBuilderProfileLogoRuntimeDataQueryVariables
            >({
                query: ReportBuilderProfileLogoRuntimeDataDocument,
                variables,
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                if (!data) {
                    throw new Error('Missing profile-logo query result for runtime data service.');
                }
                if (typeof data.reportProfileLogo === 'undefined') {
                    throw new Error('Missing profile-logo field in runtime data query result.');
                }

                return data.reportProfileLogo;
            })
        );
    };
}

export function createSystemAssetsService(
    client: ApolloClient<object>
): SystemAssetsRuntimeDataService {
    return new SystemAssetsRuntimeDataService(client);
}
